import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CARDINALITIES,
  JOIN_TYPES,
  buildJoinPayload,
  suggestColumnPairs,
  tableLabel
} from './modelGraphData';

const EMPTY_PAIR = { source_column: '', target_column: '' };

/**
 * Crear o editar una relación (join) entre dos tablas del modelo. Las
 * columnas se eligen de las reales de cada tabla. Al crear, propone los
 * pares de columnas que se llaman igual en ambas tablas.
 *
 * `onSave({ sourceTable, targetTable, payload, joinId })` es async: si
 * lanza, el error se muestra en el diálogo y no se cierra.
 */
const JoinEditorDialog = ({ open, mode = 'create', tables, initial, existingEdges = [], onClose, onSave }) => {
  const isEdit = mode === 'edit';
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [pairs, setPairs] = useState([EMPTY_PAIR]);
  const [type, setType] = useState('left');
  const [cardinality, setCardinality] = useState('many_to_one');
  const [purpose, setPurpose] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const tableById = useMemo(() => new Map(tables.map((t) => [t.table_id, t])), [tables]);
  const sourceTable = tableById.get(sourceId);
  const targetTable = tableById.get(targetId);

  // Estado inicial en cada apertura.
  useEffect(() => {
    if (!open) return;
    const join = initial?.join;
    const source = tableById.get(initial?.sourceTableId);
    const target = tableById.get(initial?.targetTableId);
    setSourceId(initial?.sourceTableId || '');
    setTargetId(initial?.targetTableId || '');
    if (join) {
      setPairs(join.on?.length ? join.on.map((p) => ({ ...p })) : [EMPTY_PAIR]);
      setType(join.type || 'left');
      setCardinality(join.cardinality || '');
      setPurpose(join.purpose || '');
    } else {
      const suggested = source && target ? suggestColumnPairs(source, target) : [];
      setPairs(suggested.length ? suggested : [EMPTY_PAIR]);
      setType('left');
      setCardinality('many_to_one');
      setPurpose('');
    }
    setError('');
    setSaving(false);
  }, [open, initial, tableById]);

  // Al cambiar de tabla (solo al crear) se vuelven a sugerir columnas.
  const changeTable = (setter) => (event) => {
    setter(event.target.value);
    const nextSource = tableById.get(setter === setSourceId ? event.target.value : sourceId);
    const nextTarget = tableById.get(setter === setTargetId ? event.target.value : targetId);
    const suggested = nextSource && nextTarget ? suggestColumnPairs(nextSource, nextTarget) : [];
    setPairs(suggested.length ? suggested : [EMPTY_PAIR]);
  };

  const updatePair = (index, field) => (event) => {
    setPairs((prev) => prev.map((pair, i) => (i === index ? { ...pair, [field]: event.target.value } : pair)));
  };

  const completePairs = pairs.filter((pair) => pair.source_column && pair.target_column);
  const duplicate = !isEdit && sourceTable && targetTable && existingEdges.some((edge) => (
    edge.data.sourceTable.table_id === sourceTable.table_id
    && edge.data.targetTable.table_id === targetTable.table_id
  ));
  const canSave = sourceTable && targetTable && completePairs.length > 0 && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave({
        sourceTable,
        targetTable,
        joinId: initial?.join?.join_id,
        payload: buildJoinPayload({ targetTable, type, cardinality, pairs, purpose })
      });
    } catch (saveError) {
      setError(saveError.message || 'No se pudo guardar la relación.');
      setSaving(false);
    }
  };

  const columnOptions = (table) => (table?.columns || []).map((column) => (
    <MenuItem key={column.column_name} value={column.column_name}>
      {column.column_name}
      <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.75rem' }}>{column.data_type}</Typography>
    </MenuItem>
  ));

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Editar relación' : 'Nueva relación'}</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select fullWidth size="small" label="Tabla origen" value={sourceId}
            onChange={changeTable(setSourceId)} disabled={isEdit || saving}
            helperText="La tabla que guarda la relación"
          >
            {tables.map((t) => <MenuItem key={t.table_id} value={t.table_id}>{tableLabel(t)}</MenuItem>)}
          </TextField>
          <TextField
            select fullWidth size="small" label="Tabla destino" value={targetId}
            onChange={changeTable(setTargetId)} disabled={isEdit || saving}
          >
            {tables.map((t) => <MenuItem key={t.table_id} value={t.table_id}>{tableLabel(t)}</MenuItem>)}
          </TextField>
        </Stack>

        {duplicate && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ya existe una relación de esta tabla origen hacia esta tabla destino. Puedes crear otra si usa columnas distintas.
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Columnas que se unen</Typography>
        {pairs.map((pair, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <TextField
              select fullWidth size="small" label={`Columna de ${sourceTable ? tableLabel(sourceTable) : 'origen'}`}
              value={pair.source_column} onChange={updatePair(index, 'source_column')}
              disabled={!sourceTable || saving}
            >
              {columnOptions(sourceTable)}
            </TextField>
            <Typography sx={{ color: 'text.secondary' }}>=</Typography>
            <TextField
              select fullWidth size="small" label={`Columna de ${targetTable ? tableLabel(targetTable) : 'destino'}`}
              value={pair.target_column} onChange={updatePair(index, 'target_column')}
              disabled={!targetTable || saving}
            >
              {columnOptions(targetTable)}
            </TextField>
            <Tooltip title="Quitar este par">
              <span>
                <IconButton
                  size="small" aria-label="Quitar par de columnas" disabled={pairs.length === 1 || saving}
                  onClick={() => setPairs((prev) => prev.filter((_, i) => i !== index))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} disabled={saving} onClick={() => setPairs((prev) => [...prev, EMPTY_PAIR])} sx={{ mb: 2 }}>
          Agregar otra columna
        </Button>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select fullWidth size="small" label="Tipo de join" value={type}
            onChange={(e) => setType(e.target.value)} disabled={saving}
            helperText={JOIN_TYPES.find((t) => t.value === type)?.help}
          >
            {JOIN_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField
            select fullWidth size="small" label="Cardinalidad" value={cardinality}
            onChange={(e) => setCardinality(e.target.value)} disabled={saving}
          >
            <MenuItem value="">Sin especificar</MenuItem>
            {CARDINALITIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
        </Stack>
        <TextField
          fullWidth multiline minRows={2} size="small" label="Propósito (opcional)"
          placeholder="Ej.: relaciona cada venta con el cliente que la realizó"
          value={purpose} onChange={(e) => setPurpose(e.target.value)} disabled={saving}
          helperText="Ayuda al asistente a saber cuándo usar esta relación."
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear relación'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JoinEditorDialog;
